/* =====================================================================
 * Entry Point
 * ===================================================================== */

/* Phase 11-4b-tenant: bootstrap the active-tenant registry BEFORE any
 * other import. The imported module's top-level body calls
 * `setActiveTenantManifest()` so shared packages (workspace-utils,
 * i18n, …) can resolve tenant-scoped storage keys at their own
 * module eval.
 *
 * IMPORTANT — must be a function call, not a side-effect or const
 * import:
 *   - A naked `import './tenant/storage'` is a side-effect-only
 *     import that Vite/Rollup are free to hoist into a secondary
 *     chunk under our `manualChunks` configuration.
 *   - A `const __MARKER__ = true` re-export gets inlined by Terser
 *     and dead-code-eliminated, allowing the same hoist.
 *   In both cases `setActiveTenantManifest()` ends up running at
 *   lazy-chunk eval time — too late, since `@auw/i18n`'s module body
 *   already called `getActiveTenantManifest()` and threw "Active
 *   tenant manifest has not been set". (2026-04-29 dev.askyour.ai
 *   blank-screen regression.)
 *
 *   Calling a real function with its return value used downstream
 *   forces the bundler to keep `./tenant/storage` in the entry
 *   chunk's eval graph and pins its eval order to "before every
 *   other import below". */
import { getBootstrappedTenantId } from './tenant/storage';
const __auwBootstrappedTenantId = getBootstrappedTenantId();
if (typeof __auwBootstrappedTenantId !== 'string' || __auwBootstrappedTenantId.length === 0) {
  throw new Error('[bootstrap] tenant registry failed to initialize');
}

import React from 'react';
import { registerB2cFeatures } from '@auw/enterprise-app/b2c-features';
import { PlatformProvider } from '@auw/platform-contracts';
import { createWebPlatform } from '@auw/platform-web';
import ReactDOM from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { AuthProviderRegistry } from '@auw/auth';
import { setTenantSnapshot } from '@auw/onprem';
import { TenantProvider } from '@auw/tenant';
import { FeatureFlagProvider, type FeatureFlagMap } from '@auw/feature-flags';
import App from './App';
import {
  queryClient,
  hydrateFromSessionStorage,
  dehydrateToSessionStorage,
} from '@/query/client';
import { initErrorMonitoring } from '@/monitoring/sentry';
import { initPerfMetricCollector } from '@/monitoring/perfMetricCollector';
import {
  AppErrorBoundary,
  SettingsModal,
  restoreCookieConsentToSink,
  setCookieConsentSink,
  setSettingsModalComponent,
} from '@auw/workspace-ui';

import { updateGA4Consent } from '@/monitoring/ga4';

/* Phase 11-4h: register host-specific mode components into shared DI
 * slots so the workspace router / chat gear button can render them
 * without a direct host-path import.
 *
 *  - SettingsModal: B2C ships the full GDPR cookie + legal-versions
 *    panel; on-prem tenants inject a lighter corporate variant or leave
 *    the slot empty (gear button silently no-ops).
 *  - DocAgent pages: use the current shared editor through the B2C feature entry. */
setSettingsModalComponent(SettingsModal);
registerB2cFeatures();

import { scheduleRuntimeFontLoading } from '@auw/workspace-utils';
import { resolveTenantManifest } from '@/tenant';
import { buildTenantAuthProviders } from '@/auth/providers';
import { AppPermissionProvider } from '@/permissions/AppPermissionProvider';

/* i18n — 앱 전역 다국어 초기화 (다른 모듈보다 먼저 로드) */
import '@auw/i18n';

/* Temml CSS — 시스템 폰트 사용 (웹폰트 로딩 불필요, 가장 가벼움) */
import 'temml/dist/Temml-Local.css';

/* Pretendard — 번들된 동적 서브셋. 공개 랜딩도 첫 페인트부터 같은 글꼴을 쓰고,
 * 필요한 유니코드 범위만 내려받는다(enterprise 앱과 같은 방식). */
import 'pretendard/dist/web/variable/pretendardvariable-dynamic-subset.css';

/* NOTE: DocAgent 전용 CSS (`@/features/docAgent/vendor/docxEditor/index.css`)
 * 는 본 파일이 아니라 실제 사용 청크에서 import 한다.
 *   - DocAgentModePage: 문서에이전트 편집 페이지
 *   - DocAgentReadOnlyDocxPreview: 통합/공통 파일 미리보기의 DOCX read-only
 *     렌더러
 * 해당 CSS 는 `.ep-root` / `.docx-*` / `.layout-*` / `.ProseMirror-*` 등
 * 에디터 전용 셀렉터로만 구성되어 DOCX 화면을 열 때만 필요하다. */

/* DocAgent dev-only 전역 디버그 훅 (window.__docAgentDebug).
 *
 * devGlobal 모듈은 `docxStack`, `pagedEditor`, `vendor/docxEditor` 등을
 * top-level 로 import 하므로 top-level 정적 import 로 두면 production 번들
 * 에도 해당 모듈 트리가 모두 포함된다. `import.meta.env.DEV` 는 런타임
 * 분기이지 tree-shake 조건이 아니라, 분기 내부의 정적 import 도 같은
 * 문제를 갖는다. 따라서 dev 에서만 동적 import 로 비동기 로드해 초기
 * 앱 번들과 분리하고, production 번들에서는 아예 진입하지 않는다. */

/* Phase 12-5b: register the GA4 dispatcher into the shared
 * cookie-consent hook BEFORE we replay any persisted state. Enterprise
 * hosts skip both calls — they ship no analytics integration. */
setCookieConsentSink(updateGA4Consent);

/* Phase 8: publish the resolved tenant to `@auw/onprem`'s module-scope
 * snapshot BEFORE any non-React loader runs (sentry/ga4/paddle/font
 * loader read it back). This is the single source of truth for tenant-
 * aware decisions outside the React tree. */
const activeTenant = resolveTenantManifest();
setTenantSnapshot(activeTenant);

initErrorMonitoring();
initPerfMetricCollector();
restoreCookieConsentToSink();

const scheduleQueryHydration = () => {
  const runHydration = () => hydrateFromSessionStorage();
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(() => {
      runHydration();
    }, { timeout: 900 });
    return;
  }
  window.setTimeout(runHydration, 0);
};

scheduleQueryHydration();
window.addEventListener('beforeunload', dehydrateToSessionStorage);

const tenantFeatureFlags = activeTenant.features as FeatureFlagMap | undefined;
const tenantAuthProviders = buildTenantAuthProviders(activeTenant);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <PlatformProvider value={createWebPlatform()}>
    <TenantProvider manifest={activeTenant}>
      <FeatureFlagProvider flags={tenantFeatureFlags}>
        <AuthProviderRegistry providers={tenantAuthProviders}>
          <QueryClientProvider client={queryClient}>
            <AppPermissionProvider>
              <AppErrorBoundary name="root-app">
                <App />
              </AppErrorBoundary>
            </AppPermissionProvider>
          </QueryClientProvider>
        </AuthProviderRegistry>
      </FeatureFlagProvider>
    </TenantProvider>
      </PlatformProvider>
  </React.StrictMode>,
);

scheduleRuntimeFontLoading();

/* DocAgent 진입 청크 prefetch
 *
 * DocAgentModePage 는 `React.lazy` 로 라우트 청크가 분리되어 있어 실제
 * 탭 진입 시점에 JS + CSS 를 받는다. 번들 크기가 크기 때문에 네트워크
 * 상태에 따라 수백 ms ~ 수 초 대기가 발생한다. 앱이 idle 해진 시점에
 * 미리 한 번 import 해 두면 실제 진입 시 캐시 히트로 즉시 렌더된다.
 * 이미 방문 중이거나 Vite 내부 캐시에 올라온 경우 추가 네트워크 요청은
 * 발생하지 않는다 (동일 Promise 재사용). 실패는 조용히 무시.
 */
